import { computed, reactive, inject } from "vue";
import {
  rotateAllBlockIds,
  rotateBlockId,
  type BlockIdRotationResult,
} from "../lib/blockIdRotation";

export const useContentManager = (resourceKeyParam?: string) => {
  const logger = useLogger("useContentManager");
  const features = inject("render-features", { editor: false, resourceKey: "default" });
  const resourceKey = resourceKeyParam ?? features.resourceKey ?? "default";
  const canMutate = () => features.editor !== true || features.editable !== false;

  const contentStateMap = useState<Record<string, any[]>>("content-state-map", () => ({}));
  const contentTypeMap = useState<Record<string, string | null>>("content-type-map", () => ({}));

  if (!contentStateMap.value[resourceKey]) {
    contentStateMap.value[resourceKey] = [];
  }
  if (!(resourceKey in contentTypeMap.value)) {
    contentTypeMap.value[resourceKey] = null;
  }

  const contentState = computed({
    get: () => contentStateMap.value[resourceKey],
    set: (val) => {
      contentStateMap.value[resourceKey] = val;
    },
  });
  const contentType = computed({
    get: () => contentTypeMap.value[resourceKey],
    set: (val) => {
      contentTypeMap.value[resourceKey] = val;
    },
  });

  const generateId = () => crypto.randomUUID();

  const setContent = (initialContent: any[]) => {
    logger.info(
      "setContent called with",
      initialContent?.length,
      "items for key",
      resourceKey,
    );
    contentStateMap.value[resourceKey] = initialContent?.map((item) => {
      if (!item.id) {
        item.id = generateId();
        logger.debug("Generated new ID for item:", item.id);
      }
      if (item.children && Array.isArray(item.children)) {
        item.children = item.children.map((child) => {
          if (!child.id) {
            child.id = generateId();
            logger.debug("Generated new ID for child:", child.id);
          }
          return reactive(child);
        });
      }
      return reactive(item);
    });
    logger.info("Content state updated for key", resourceKey);
  };

  const setContentType = (type: string | null) => {
    logger.info("setContentType called with", type, "for key", resourceKey);
    contentTypeMap.value[resourceKey] = type;
  };

  const getContentById = (id: string) => {
    logger.info(
      "getContentById called for ID=",
      id,
      "in key",
      resourceKey,
    );
    const items = contentStateMap.value[resourceKey] ?? [];
    const item = findItemById(id, items);
    logger.debug("getContentById found:", item);
    return item;
  };

  const findItemById = (
    id: string,
    items: any[],
    visited = new Set(),
  ): any => {
    if (!Array.isArray(items)) return null; // guard

    for (const item of items) {
      if (item.id === id) {
        logger.debug("findItemById found item with ID=", id);
        return item;
      }
      if (visited.has(item)) continue;
      visited.add(item);

      if (Array.isArray(item.children) && item.children.length) {
        const found = findItemById(id, item.children, visited);
        if (found) return found;
      }
    }
    return null;
  };

  const removeItemById = (id: string, items: any[]): any => {
    if (!canMutate()) return null;
    for (const [index, item] of items.entries()) {
      if (item.id === id) {
        logger.info("Item removed:", item);
        items.splice(index, 1);
        return item;
      }
      if (item.children && item.children.length > 0) {
        const found = removeItemById(id, item.children);
        if (found) {
          return found;
        }
      }
    }
    logger.warn("Failed to remove item for ID:", id);
    return null;
  };

  const isSelfOrDescendant = (node: any, targetId: string): boolean => {
    if (!node) return false;
    if (node.id === targetId) return true;
    if (Array.isArray(node.children)) {
      for (const child of node.children) {
        if (isSelfOrDescendant(child, targetId)) return true;
      }
    }
    return false;
  };

  const moveItem = (
    draggedItemId: string,
    targetId: string,
    insertIndex?: number,
  ) => {
    if (!canMutate()) return;
    const items = contentStateMap.value[resourceKey] ?? [];
    if (!draggedItemId || !targetId || draggedItemId === targetId) {
      logger.warn("Invalid move operation");
      return;
    }

    logger.info(
      "Attempting to move item",
      draggedItemId,
      "to",
      targetId,
      "at index",
      insertIndex,
      "in key",
      resourceKey,
    );

    // Validate BEFORE mutating so we never lose the dragged item on a bad drop.
    const draggedItem = findItemById(draggedItemId, items);
    const targetItem = findItemById(targetId, items);
    if (!draggedItem || !targetItem) {
      logger.warn("Move failed: Dragged or Target item not found");
      return;
    }

    // Never drop a block into itself or one of its own descendants.
    if (isSelfOrDescendant(draggedItem, targetId)) {
      logger.warn("Move rejected: target is inside the dragged subtree");
      return;
    }

    // When reordering within the same parent, account for the index shift
    // caused by removing the item before re-inserting it.
    const currentParent = findParentOf(draggedItemId, items);
    const sameParent = currentParent && currentParent.id === targetId;
    const originalIndex = sameParent
      ? currentParent.children.indexOf(draggedItem)
      : -1;

    removeItemById(draggedItemId, items);

    targetItem.children = targetItem.children || [];
    let idx = insertIndex;
    if (
      sameParent &&
      typeof idx === "number" &&
      originalIndex !== -1 &&
      originalIndex < idx
    ) {
      idx = idx - 1;
    }

    if (typeof idx === "number" && idx >= 0 && idx <= targetItem.children.length) {
      targetItem.children.splice(idx, 0, draggedItem);
      logger.debug("Inserted item at index", idx);
    } else {
      targetItem.children.push(draggedItem);
      logger.debug("Appended item to target's children");
    }
    logger.info("Item moved successfully");
  };

  const moveToRoot = (draggedItemId: string, insertIndex?: number) => {
    if (!canMutate()) return;
    const items = contentStateMap.value[resourceKey] ?? [];
    if (!draggedItemId) {
      logger.warn("Invalid move-to-root operation");
      return;
    }

    const draggedItem = findItemById(draggedItemId, items);
    if (!draggedItem) {
      logger.warn("Move-to-root failed: dragged item not found");
      return;
    }

    const originalIndex = items.findIndex((b: any) => b.id === draggedItemId);
    removeItemById(draggedItemId, items);

    let idx = insertIndex;
    if (
      originalIndex !== -1 &&
      typeof idx === "number" &&
      originalIndex < idx
    ) {
      idx = idx - 1;
    }

    if (typeof idx === "number" && idx >= 0 && idx <= items.length) {
      items.splice(idx, 0, draggedItem);
    } else {
      items.push(draggedItem);
    }
    logger.info("Item moved to root successfully");
  };

  const updateContentById = (id: string, updatedContent: any) => {
    if (!canMutate()) return;
    const items = contentStateMap.value[resourceKey] ?? [];
    const item = findItemById(id, items);
    if (item) {
      logger.info(
        "updateContentById called for ID=",
        id,
        "with:",
        updatedContent,
        "in key",
        resourceKey,
      );
      Object.assign(item, updatedContent);
      logger.debug("New item shape:", JSON.stringify(item, null, 2));
    } else {
      logger.warn("updateContentById failed: Item not found for ID=", id);
    }
  };

  const renameContentId = (
    oldId: string,
    newId: string,
  ): BlockIdRotationResult => {
    if (!canMutate()) {
      return { ok: false, error: "not-found" };
    }
    const items = contentStateMap.value[resourceKey] ?? [];
    const result = rotateBlockId(items, oldId, newId);
    if (!result.ok) {
      logger.warn("renameContentId failed:", result.error, {
        oldId,
        newId,
        duplicateIds: result.duplicateIds,
      });
      return result;
    }
    logger.info("renameContentId:", oldId, "->", newId.trim());
    return result;
  };

  const rotateAllContentIds = () => {
    if (!canMutate()) {
      return { ok: false, error: "not-found" };
    }
    const items = contentStateMap.value[resourceKey] ?? [];
    const result = rotateAllBlockIds(items, generateId);
    if (!result.ok) {
      logger.warn("rotateAllContentIds failed:", result.error, {
        duplicateIds: result.duplicateIds,
      });
      return result;
    }
    logger.info("rotateAllContentIds rotated", result.rotatedCount, "IDs");
    return result;
  };

  const commitChanges = () => {
    logger.info("commitChanges called for key", resourceKey);
    const items = contentStateMap.value[resourceKey] ?? [];
    logger.info("Committing changes:", JSON.stringify(items, null, 2));
    return items;
  };

  function findParentOf(childId: string, items: any[]): any | null {
    for (const item of items) {
      if (Array.isArray(item.children)) {
        const found = item.children.find((child: any) => child.id === childId);
        if (found) {
          return item;
        }
        const deeper = findParentOf(childId, item.children);
        if (deeper) return deeper;
      }
    }
    return null;
  }

  function deepGet(obj: any, path: string): any {
    const parts = path.split(".");
    let curr = obj;
    for (const part of parts) {
      if (curr && typeof curr === "object" && part in curr) {
        curr = curr[part];
      } else {
        return undefined;
      }
    }
    return curr;
  }

  function getResolvedDataPath(id: string): string[] {
    const items = contentStateMap.value[resourceKey] ?? [];
    const item = findItemById(id, items);
    if (!item) return [];
    if (!item.dataPath || !Array.isArray(item.dataPath)) {
      return [];
    }
    const resolvedPath: string[] = [];
    for (const segment of item.dataPath) {
      const isPlaceholder =
        typeof segment === "string" && /^\{\{.*\}\}$/.test(segment);
      if (!isPlaceholder) {
        resolvedPath.push(segment);
        continue;
      }
      const placeholderName = segment.slice(2, -2).trim();
      if (placeholderName === "parent") {
        const parent = findParentOf(id, items);
        if (!parent) continue;
        const parentPath = getResolvedDataPath(parent.id);
        resolvedPath.push(...parentPath);
      } else {
        const fieldValue = deepGet(item, placeholderName);
        if (Array.isArray(fieldValue)) {
          resolvedPath.push(...fieldValue);
        } else if (
          typeof fieldValue === "string" ||
          typeof fieldValue === "number"
        ) {
          resolvedPath.push(String(fieldValue));
        } else if (fieldValue != null) {
          resolvedPath.push(String(fieldValue));
        }
      }
    }
    return resolvedPath;
  }

  return {
    contentState,
    contentType,
    contentStateMap,
    contentTypeMap,
    generateId,
    setContent,
    setContentType,
    getContentById,
    findItemById,
    updateContentById,
    removeItemById,
    moveItem,
    moveToRoot,
    commitChanges,
    findParentOf,
    getResolvedDataPath,
    renameContentId,
    rotateAllContentIds,
  };
};
